import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useSpring, animated } from '@react-spring/web';

import { FiX, FiLock } from 'react-icons/fi';
import orzelKsiazkaIcon from '../../img/icons/orzel_ksiazka.png';

import odznakaBrak from '../../img/icons/odznaka_brak.png';
import odznakaBrazowa from '../../img/icons/odznaka_brazowa.png';
import odznakaSrebrna from '../../img/icons/odznaka_srebrna.png';
import odznakaZlota from '../../img/icons/odznaka_zlota.png';

import { getMaturaDate } from '../../utils/configUtils';

const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};
const modalVariants = {
  hidden: { opacity: 0, y: 50, scale: 0.9 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { type: 'spring', stiffness: 150, damping: 20 } },
  exit: { opacity: 0, y: 50, scale: 0.9, transition: { duration: 0.2 } },
};
const stepVariants = {
  enter: (direction) => ({ x: direction > 0 ? 100 : -100, opacity: 0 }),
  center: { x: 0, opacity: 1, transition: { duration: 0.3 } },
  exit: (direction) => ({ x: direction < 0 ? 100 : -100, opacity: 0, transition: { duration: 0.3 } })
};

const badgeConfig = [
  { level: "Brązowa", days: 1, image: odznakaBrazowa },
  { level: "Srebrna", days: 6, image: odznakaSrebrna },
  { level: "Złota", days: 31, image: odznakaZlota },
];
const getBadgeStatus = (streak) => {
  let currentBadge = { image: odznakaBrak, textColor: null, level: "Brak" };
  if (streak >= badgeConfig[2].days) {
    currentBadge = { image: badgeConfig[2].image, textColor: "#B8860B", level: badgeConfig[2].level };
  } else if (streak >= badgeConfig[1].days) {
    currentBadge = { image: badgeConfig[1].image, textColor: "#606060", level: badgeConfig[1].level };
  } else if (streak >= badgeConfig[0].days) {
    currentBadge = { image: badgeConfig[0].image, textColor: "#8B4513", level: badgeConfig[0].level };
  }
  return currentBadge;
};

const AnimatedMetricSimple = ({ value, suffix = "" }) => {
    const { number } = useSpring({
      from: { number: 0 },
      to: { number: value },
      delay: 200,
      config: { duration: 1500 },
    });
    return (
        <animated.span className="text-2xl font-bold font-apotek text-[#233855] leading-none">
          {number.to((n) => `${Math.round(n)}${suffix}`)}
        </animated.span>
    );
};

const StatCard = ({ label, content, fullWidth = false }) => {
    return (
        <div className={`bg-gray-50 border border-gray-200 rounded-xl p-3 flex flex-col items-center justify-center relative overflow-hidden shadow-inner ${fullWidth ? 'col-span-2 py-4' : ''}`}>
            <div className="absolute top-2 right-2 text-[#E09F2E] opacity-80">
                <FiLock size={16} />
            </div>
            <div className="flex flex-col items-center justify-center z-10 w-full">
                {content}
                <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mt-2">
                    {label}
                </div>
            </div>
        </div>
    );
};

const PaywallPopup = ({ isOpen, onClose, onSubscribe, userData }) => {

    const navigate = useNavigate();
    const [[step, direction], setStep] = useState([1, 0]);
    const [countdown, setCountdown] = useState(null);
    const maturaDateTarget = getMaturaDate();

    useEffect(() => {
        let timer;
        if (step === 2) {
          const calculateTimeLeft = () => {
            const now = new Date();
            const difference = maturaDateTarget - now;
            let timeLeft = {};
            if (difference > 0) {
              timeLeft = {
                days: Math.floor(difference / (1000 * 60 * 60 * 24)),
                hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
                minutes: Math.floor((difference / 1000 / 60) % 60),
                seconds: Math.floor((difference / 1000) % 60),
                isTimeUp: false
              };
            } else {
              timeLeft = { days: 0, hours: 0, minutes: 0, seconds: 0, isTimeUp: true };
            }
            return timeLeft;
          };
          setCountdown(calculateTimeLeft());
          timer = setInterval(() => {
            setCountdown(calculateTimeLeft());
          }, 1000);
        }
        return () => clearInterval(timer);
    }, [step, maturaDateTarget]);

    if (!isOpen) return null;

    const dataToDisplay = userData || {
      problems_solved_total: 75,
      total_study_time_minutes: 185,
      current_streak: 14,
    };
    const streakBadge = getBadgeStatus(dataToDisplay.current_streak);

    const dynamicHeightClass = step === 1 ? 'h-[520px]' : 'h-[460px]';

    const paywallSteps = [
        {
            step: 1,
            component: (
                <div className="flex flex-col items-center justify-start h-full w-full px-2 pt-2">

                    <img
                        src={orzelKsiazkaIcon}
                        alt="Orzeł z książką"
                        className="h-24 w-24 mb-4"
                    />

                    <div className="text-center mb-5">
                        <h2 className="text-2xl font-apotek tracking-wider text-[#233855] mb-1 uppercase font-bold">
                             Koniec darmowego dostępu 🔒
                        </h2>
                        <p className="text-xs text-gray-500 leading-tight">
                            Bez aktywnego konta Twoje statystyki zostaną zablokowane.
                        </p>
                    </div>

                    <div className="w-full grid grid-cols-2 gap-3 mb-2">

                        <StatCard
                            fullWidth={true}
                            label="odznaka i Dni z rzędu zagrożone"
                            content={
                                <div className="relative w-20 h-20">
                                    <motion.div
                                        initial={{ scale: 0.8, opacity: 0 }}
                                        animate={{ scale: 1, opacity: 1 }}
                                        transition={{ delay: 0.2, type: 'spring', stiffness: 150, damping: 15 }}
                                        className="w-full h-full"
                                    >
                                        <img
                                            src={streakBadge.image}
                                            alt={streakBadge.level}
                                            className="w-full h-full object-contain opacity-90 grayscale-[0.3]"
                                        />
                                        {dataToDisplay.current_streak > 0 && (
                                            <div
                                                className="absolute flex top-1/2 left-1/2 -translate-x-[55%] -translate-y-[50%] items-center justify-center text-3xl font-apotek"
                                                style={{ color: streakBadge.textColor || '#233855' }}
                                            >
                                                {dataToDisplay.current_streak}
                                            </div>
                                        )}
                                    </motion.div>
                                </div>
                            }
                        />

                        <StatCard
                            label="Zadania"
                            content={<AnimatedMetricSimple value={dataToDisplay.problems_solved_total} />}
                        />

                        <StatCard
                            label="Minuty"
                            content={<AnimatedMetricSimple value={dataToDisplay.total_study_time_minutes} suffix=" min" />}
                        />
                    </div>

                </div>
            )
        },
        {
            step: 2,
            title: 'To dopiero początek!',
            content: 'Ta rozgrzewka pokazała, na co Cię stać! Zachowaj swoje postępy i narzędzie, które pomaga Ci budować matematyczną pewność siebie.',
        }
    ];

    const paginate = (newDirection) => {
        setStep([step + newDirection, newDirection]);
    };

    const currentStepData = paywallSteps.find(s => s.step === step);
    const isLastStep = step === paywallSteps.length;

    const handleSubscribeAction = () => {
        onClose();
        if (onSubscribe) {
            onSubscribe();
        } else {
            navigate('/upgrade');
        }
    };

    const formatTime = (time) => String(time).padStart(2, '0');

  return ReactDOM.createPortal(
    <motion.div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9998] flex items-center justify-center p-4"
      variants={backdropVariants}
      initial="hidden"
      animate="visible"
      exit="hidden"
      onClick={onClose}
    >
      <motion.div
        className="bg-white rounded-xl lg:rounded-2xl shadow-2xl w-full max-w-md mx-auto overflow-hidden relative transition-all duration-300"
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        onClick={(e) => e.stopPropagation()}
      >
        {step !== 1 && (
            <button
            onClick={onClose}
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 z-10"
            >
            <FiX size={24} />
            </button>
        )}

        <div className={`relative ${dynamicHeightClass} overflow-hidden transition-all duration-300`}>
          <AnimatePresence initial={false} custom={direction} mode="wait">
            <motion.div
              key={step}
              className="absolute inset-0 p-6 flex flex-col justify-center items-center text-center"
              custom={direction}
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
            >

              {currentStepData.title && (
                  <h3 className="text-xl font-apotek uppercase tracking-widest font-semibold mb-4 text-[#233855]">
                    {currentStepData.title}
                  </h3>
              )}

              {currentStepData.component && (
                <div className="w-full">
                  {currentStepData.component}
                </div>
              )}

              {currentStepData.content && (
                <p className="text-sm text-gray-600 mt-4">
                  <span dangerouslySetInnerHTML={{ __html: currentStepData.content }} />
                </p>
              )}

              {step === 2 && countdown && (
                <div className="mt-4 w-full">
                  <p className="text-sm text-gray-600 mb-3">Do Twojej matury pozostało:</p>
                  {countdown.isTimeUp ? (
                    <div className="text-2xl font-bold text-[#233855]">Matura już trwa! Powodzenia!</div>
                  ) : (
                    <div className="flex justify-center gap-2 md:gap-4">
                      <div className="text-center bg-gray-100 p-2 rounded-lg w-16 shadow-inner">
                        <div className="text-2xl font-bold font-apotek text-[#233855]">{formatTime(countdown.days)}</div>
                        <div className="text-xs text-gray-500 uppercase">Dni</div>
                      </div>
                      <div className="text-center bg-gray-100 p-2 rounded-lg w-16 shadow-inner">
                        <div className="text-2xl font-bold font-apotek text-[#233855]">{formatTime(countdown.hours)}</div>
                        <div className="text-xs text-gray-500 uppercase">Godz</div>
                      </div>
                      <div className="text-center bg-gray-100 p-2 rounded-lg w-16 shadow-inner">
                        <div className="text-2xl font-bold font-apotek text-[#233855]">{formatTime(countdown.minutes)}</div>
                        <div className="text-xs text-gray-500 uppercase">Min</div>
                      </div>
                      <div className="text-center bg-gray-100 p-2 rounded-lg w-16 shadow-inner">
                          <div className="text-2xl font-bold font-apotek text-[#233855]">{formatTime(countdown.seconds)}</div>
                        <div className="text-xs text-gray-500 uppercase">Sek</div>
                      </div>
                    </div>
                  )}
                </div>
              )}

            </motion.div>
          </AnimatePresence>
        </div>

        <div className="p-6 bg-gray-50/50 flex items-center justify-between">
          <div className="flex gap-2">
            {paywallSteps.map((s) => (
              <div
                key={s.step}
                className={`w-2 h-2 rounded-full transition-all ${
                  s.step === step ? 'bg-[#233855]' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>

          <div className="flex gap-3">
            {step > 1 && (
              <button
                type="button"
                onClick={() => paginate(-1)}
                className="rounded-lg px-4 py-2 text-sm font-apotek uppercase tracking-widest text-gray-600 hover:bg-gray-100"
              >
                Wstecz
              </button>
            )}

            <button
              type="button"
              onClick={isLastStep ? handleSubscribeAction : () => paginate(1)}
              className={`flex justify-center rounded-lg px-4 py-2 text-sm font-apotek uppercase tracking-widest text-slate-100 shadow-lg transition-colors ${
                isLastStep
                ? 'bg-[#E09F2E] hover:bg-[#c98e28]'
                : 'bg-[#233855] hover:bg-[#345081]'
              }`}
            >
              {isLastStep ? 'Przedłuż dostęp!' : 'Dalej'}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
};

export default PaywallPopup;
